import React, {useCallback, useMemo} from 'react';
import {Alert, Box, Stack, styled} from '@mui/material';
import {
  Body,
  designSystemToken,
  Icon,
  IconName,
  Label,
} from '@lightricks/react-design-system';
import LEGACY_COLORS from '@/config/legacyColors';
import NotificationBannerState from './NotificationBannerState';

// eslint-disable-next-line @typescript-eslint/naming-convention
enum NotificationContentType {
  TITLE_AND_DESCRIPTION = 'titleAndDescription',
  CUSTOM_RENDER = 'customRender',
}

interface NotificationContent {
  type: NotificationContentType.TITLE_AND_DESCRIPTION;
  title: string;
  description: string;
  inline?: boolean;
}

interface NotificationCustomRenderContent {
  type: NotificationContentType.CUSTOM_RENDER;
  customRender: () => React.ReactNode;
}

interface NotificationBannerProps {
  testID?: string;
  state: NotificationBannerState;
  content: NotificationCustomRenderContent | NotificationContent;
  onRemove?: (event: React.MouseEvent<HTMLElement>) => void;
}

const IconContainer = styled(Box)(() => ({
  cursor: 'pointer',
}));

const StyledAlert = styled(Alert)({
  'padding': '8px 12px',
  'borderRadius': '12px',
  'border': 'none',
  '& .MuiAlert-icon': {
    marginRight: '8px',
  },
  '& .MuiAlert-message': {
    width: '100%',
    alignContent: 'center',
  },
});

const STATE_ICON_MAPPING: Record<NotificationBannerState, string> = {
  [NotificationBannerState.SUCCESS]: 'Actions-Patch-Accept-Line',
  [NotificationBannerState.ERROR]: 'Navigation-Attention-Line',
  [NotificationBannerState.INFO]: 'Actions-Info-Line',
  [NotificationBannerState.WARNING]: 'Navigation-Attention-Line',
};

function NotificationBanner({
  testID = 'notification-banner',
  state,
  content,
  onRemove,
}: NotificationBannerProps) {
  const getAlertSeverity = () => {
    switch (state) {
      case NotificationBannerState.SUCCESS:
        return 'success';
      case NotificationBannerState.ERROR:
        return 'error';
      case NotificationBannerState.INFO:
        return 'info';
      case NotificationBannerState.WARNING:
        return 'warning';
      default:
        return 'info';
    }
  };

  const getBackgroundColor = () => {
    switch (state) {
      case NotificationBannerState.SUCCESS:
        return designSystemToken('semantic.bg.success-secondary');
      case NotificationBannerState.ERROR:
        return designSystemToken('semantic.bg.danger-secondary');
      case NotificationBannerState.INFO:
        return designSystemToken('semantic.bg.brand-secondary');
      case NotificationBannerState.WARNING:
        return designSystemToken('semantic.bg.warning-secondary');
      default:
        return designSystemToken('semantic.bg.secondary');
    }
  };

  const getTextColor = useCallback(() => {
    switch (state) {
      case NotificationBannerState.SUCCESS:
        return LEGACY_COLORS.DARK_GREEN;
      case NotificationBannerState.ERROR:
        return designSystemToken('semantic.fg.danger');
      case NotificationBannerState.INFO:
        return designSystemToken('semantic.fg.brand');
      case NotificationBannerState.WARNING:
        return designSystemToken('semantic.fg.warning');
      default:
        return designSystemToken('semantic.fg.secondary');
    }
  }, [state]);

  const iconMapping = useMemo(() => {
    const icon = (
      <Icon
        appearance="neutral"
        size="medium"
        name={STATE_ICON_MAPPING[state] as IconName}
        color={getTextColor()}
      />
    );
    return {
      success: <IconContainer onClick={onRemove}>{icon}</IconContainer>,
      error: <IconContainer onClick={onRemove}>{icon}</IconContainer>,
      info: <IconContainer onClick={onRemove}>{icon}</IconContainer>,
      warning: <IconContainer onClick={onRemove}>{icon}</IconContainer>,
    };
  }, [state, onRemove, getTextColor]);

  const renderContent = () => {
    switch (content.type) {
      case NotificationContentType.CUSTOM_RENDER:
        return content.customRender();
      case NotificationContentType.TITLE_AND_DESCRIPTION:
        if (content.inline) {
          return (
            <Body
              size="md"
              color={getTextColor()}
              sx={{
                '& strong': {
                  fontWeight: 700,
                },
              }}
            >
              <strong>{content.title}</strong> {content.description}
            </Body>
          );
        }

        return (
          <>
            <Label size="lg" color={getTextColor()}>
              {content.title}
            </Label>
            <Body size="md" color={getTextColor()}>
              {content.description}
            </Body>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <StyledAlert
      data-testid={testID}
      iconMapping={iconMapping}
      severity={getAlertSeverity()}
      variant="standard"
      sx={{
        'backgroundColor': getBackgroundColor(),
        '& .MuiAlert-icon': {
          color: getTextColor(),
        },
      }}
    >
      <Stack direction="column">{renderContent()}</Stack>
    </StyledAlert>
  );
}

export default NotificationBanner;
export {NotificationContentType};
export type {NotificationContent, NotificationCustomRenderContent};
